import { FC, useMemo, useRef, useEffect } from "react";
import {
  styled,
  Button,
  Alert,
  Table,
  ColumnProps,
  SelectionState,
  SelectionStatus,
  TableInstance,
  TableProps,
  Tooltip,
  Operators,
} from "@monolith-forensics/monolith-ui";
import {
  ImportFieldDefinition,
  ImportRowValidation,
} from "../DataImport.types";
import { resolveSelectedRowIndexes } from "../utils/resolveSelectedRowIndexes";
import { CheckCircle, XCircle, AlertTriangle } from "lucide-react";

interface DataPreviewStepProps {
  fields: ImportFieldDefinition[];
  validatedRows: ImportRowValidation[];
  selectionState: SelectionState;
  onSelectionChange: (selectionState: SelectionState) => void;
  isResolving?: boolean;
  previewTableProps?: TableProps;
}

const DataPreviewStep: FC<DataPreviewStepProps> = ({
  fields,
  validatedRows,
  selectionState,
  onSelectionChange,
  isResolving,
  previewTableProps,
}) => {
  const tableInstanceRef = useRef<TableInstance>();
  const initialSyncDone = useRef(false);

  const mappedFields = useMemo(
    () =>
      fields.filter((f) =>
        validatedRows.some(
          (r) => r.data[f.key] !== null && r.data[f.key] !== undefined
        )
      ),
    [fields, validatedRows]
  );

  const displayFields = mappedFields.length > 0 ? mappedFields : fields;

  const fieldLabelMap = useMemo(
    () => Object.fromEntries(fields.map((f) => [f.key, f.label])),
    [fields]
  );
  const selectDisplayMapByField = useMemo(() => {
    return fields.reduce<Map<string, Map<string | number, string>>>(
      (acc, field) => {
        if (field.type !== "select" || !field.options) return acc;

        const valueToLabel = new Map<string | number, string>();
        field.options.forEach((option) => {
          valueToLabel.set(option.value, option.label);
          valueToLabel.set(String(option.value), option.label);
        });

        acc.set(field.key, valueToLabel);
        return acc;
      },
      new Map()
    );
  }, [fields]);

  const stats = useMemo(() => {
    const total = validatedRows.length;
    const withErrors = validatedRows.filter(
      (r) => Object.keys(r.errors).length > 0
    ).length;
    const withWarnings = validatedRows.filter(
      (r) =>
        Object.keys(r.warnings).length > 0 && Object.keys(r.errors).length === 0
    ).length;
    const valid = total - withErrors - withWarnings;
    return { total, valid, withErrors, withWarnings };
  }, [validatedRows]);

  const selectedRowIndexes = useMemo(
    () => resolveSelectedRowIndexes(selectionState, validatedRows),
    [selectionState, validatedRows]
  );

  const tableData = useMemo(() => {
    return validatedRows.map((row) => ({
      ...row.data,
      _rowIndex: row.rowIndex,
      _isValid: row.isValid ? 1 : 0,
      _status:
        Object.keys(row.errors).length > 0
          ? "error"
          : Object.keys(row.warnings).length > 0
          ? "warning"
          : "valid",
      _errors: row.errors,
      _warnings: row.warnings,
      _isSelected: selectedRowIndexes.has(row.rowIndex),
    }));
  }, [validatedRows, selectedRowIndexes]);

  // Sync selection from parent into the Table's internal state
  useEffect(() => {
    const instance = tableInstanceRef.current;
    if (!instance || tableData.length === 0 || initialSyncDone.current) return;
    initialSyncDone.current = true;

    instance.clearSelections();
    if (selectionState.totalSelected === tableData.length) {
      instance.selectAllRows();
    } else {
      tableData.forEach((row) => {
        if (row._isSelected) instance.selectRow(row);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData]);

  const handleSelectAllValid = () => {
    const validRows = tableData.filter((row) => row._isValid);
    const allValid = validRows.length === tableData.length;
    onSelectionChange({
      selectedRowKeys: validRows.map((row) => String(row._rowIndex)),
      excludedRowKeys: [],
      selectionStatus: allValid
        ? SelectionStatus.All
        : SelectionStatus.SomeIncluded,
      totalSelected: validRows.length,
    });
    initialSyncDone.current = false;
  };

  const handleSelectAll = () => {
    tableInstanceRef.current?.selectAllRows();
  };

  const handleDeselectAll = () => {
    tableInstanceRef.current?.clearSelections();
  };

  const columnProps: ColumnProps[] = useMemo(() => {
    const cols: ColumnProps[] = [
      {
        dataField: "_rowIndex",
        caption: "#",
        width: 50,
        enableResize: false,
        enableReorder: false,
        enableSorting: false,
        render: ({ rowData }: { rowData: any }) => (
          <RowNumber>{rowData._rowIndex + 1}</RowNumber>
        ),
      },
      {
        dataField: "_isValid",
        caption: "Status",
        width: 70,
        enableResize: false,
        enableReorder: false,
        enableSorting: false,
        render: ({ rowData }: { rowData: any }) => {
          const hasErrors = Object.keys(rowData._errors).length > 0;
          const hasWarnings = Object.keys(rowData._warnings).length > 0;
          return (
            <Tooltip
              content={
                hasErrors ? (
                  <div>
                    <div>Errors:</div>
                    <div>
                      {Object.entries(rowData._errors).map(([key, value]) => (
                        <div key={key}>
                          {fieldLabelMap[key] ?? key}: {String(value)}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : hasWarnings ? (
                  <div>
                    <div>Warnings:</div>
                    <div>
                      {Object.entries(rowData._warnings).map(([key, value]) => (
                        <div key={key}>
                          {fieldLabelMap[key] ?? key}: {String(value)}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    <span>Valid</span>
                  </div>
                )
              }
              delayDuration={0}
            >
              <span>
                {hasErrors ? (
                  <XCircle size={16} color="#f44336" />
                ) : hasWarnings ? (
                  <AlertTriangle size={16} color="#ff9800" />
                ) : (
                  <CheckCircle size={16} color="#4caf50" />
                )}
              </span>
            </Tooltip>
          );
        },
      },
    ];

    if (previewTableProps) {
      cols.push(...previewTableProps.columnProps);
    } else {
      cols.push(
        ...displayFields.map((field) => ({
          dataField: field.key,
          caption: field.label,
          enableSorting: false,
          enableReorder: false,
          render: ({ rowData }: { rowData: any }) => {
            const error = rowData._errors[field.key];
            const warning = rowData._warnings[field.key];
            const value = rowData[field.key];
            const selectDisplayValue =
              field.type === "select"
                ? selectDisplayMapByField.get(field.key)?.get(value) ??
                  selectDisplayMapByField.get(field.key)?.get(String(value)) ??
                  value
                : value;
            const displayValue = field.displayValue
              ? field.displayValue(selectDisplayValue, rowData)
              : selectDisplayValue;
            return (
              <CellContent>
                <CellValue $hasError={!!error} $hasWarning={!!warning}>
                  {displayValue !== null && displayValue !== undefined
                    ? String(displayValue)
                    : ""}
                </CellValue>
              </CellContent>
            );
          },
        }))
      );
    }

    return cols;
  }, [
    displayFields,
    fieldLabelMap,
    selectDisplayMapByField,
    previewTableProps,
  ]);

  if (isResolving) {
    return (
      <Container>
        <ResolvingMessage>Processing and validating data...</ResolvingMessage>
      </Container>
    );
  }

  return (
    <Container>
      {stats.withErrors > 0 && (
        <Alert variant="warning" title="Import Issues" showCloseButton={true}>
          Rows with errors will not be imported unless the issues are resolved.
          You can deselect invalid rows to proceed with the valid ones.
        </Alert>
      )}
      <SummaryBar>
        <SummaryStats>
          <StatItem>
            <CheckCircle size={14} color="#4caf50" />
            <span>{stats.valid} valid</span>
          </StatItem>
          {stats.withErrors > 0 && (
            <StatItem>
              <XCircle size={14} color="#f44336" />
              <span>{stats.withErrors} with errors</span>
            </StatItem>
          )}
          {stats.withWarnings > 0 && (
            <StatItem>
              <AlertTriangle size={14} color="#ff9800" />
              <span>{stats.withWarnings} with warnings</span>
            </StatItem>
          )}
          <StatItem>
            <span>
              {selectionState.totalSelected} of {stats.total} selected for
              import
            </span>
          </StatItem>
        </SummaryStats>
        <SummaryActions>
          <Button size="xs" variant="subtle" onClick={handleSelectAllValid}>
            Select Valid
          </Button>
          <Button size="xs" variant="subtle" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button size="xs" variant="subtle" onClick={handleDeselectAll}>
            Deselect All
          </Button>
        </SummaryActions>
      </SummaryBar>
      <TableWrapper>
        <Table
          {...previewTableProps}
          data={tableData}
          // totalRecords={tableData.length}
          keyField="_rowIndex"
          columnProps={columnProps}
          tableInstanceRef={tableInstanceRef}
          virtualized
          enableColumnResize={true}
          enableColumnReorder={false}
          enableSorting={true}
          enableSelection
          selectionState={selectionState}
          onSelectionChange={onSelectionChange}
          tableMenuOptions={{
            enabled: true,
            searchOptions: {
              enabled: true,
            },
            tableCountOptions: {
              recordsTotalEnabled: true,
              selectionTotalEnabled: true,
            },
            columnSelectorOptions: {
              enabled: true,
            },
            filterOptions: {
              enabled: true,
              filterDefinitions: [
                {
                  label: "Status",
                  dataField: "_status",
                  inputType: "multiselect",
                  operators: [Operators.In, Operators.NIn],
                  selectOptions: [
                    { label: "Valid", value: "valid" },
                    { label: "Warning", value: "warning" },
                    { label: "Invalid", value: "error" },
                  ],
                },
              ],
            },
          }}
        />
      </TableWrapper>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
`;

const ResolvingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: ${(props: any) => props.theme.palette.text.secondary};
  font-size: 14px;
`;

const SummaryBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const SummaryStats = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  font-size: 13px;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SummaryActions = styled.div`
  display: flex;
  gap: 4px;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 300px;
  border-radius: 6px;
  overflow: hidden;
  flex: 1 1 auto;
`;

const CellContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CellValue = styled.div<{ $hasError?: boolean; $hasWarning?: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(props) =>
    props.$hasError ? "#f44336" : props.$hasWarning ? "#ff9800" : "inherit"};
`;

const RowNumber = styled.span`
  color: ${(props: any) => props.theme.palette.text.secondary};
  font-size: 12px;
`;

export default DataPreviewStep;
